import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  CATEGORY_LABELS, PRIORITIES, PRIORITY_LABELS, QUICK_VIEWS, STATUS_LABELS, canCreateTickets, formatDateTime, ticketErrorMessage,
} from "../lib/tickets";
import { DelayCell, PriorityBadge, TicketStatusBadge, ToAttachBadge } from "../components/tickets/TicketBits";
import TicketStats from "../components/tickets/TicketStats";
import { hasPermission } from "../lib/permissions";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { AlertTriangle, ChevronRight, Download, Loader2, Plus, RefreshCw, Search, SlidersHorizontal, Ticket, X } from "lucide-react";

const ALL = "__all__";
const PAGE_SIZE = 50;
const STATES = [{ id: "open", label: "Ouverts", short: "Ouverts" }, { id: "done", label: "Résolus et clos", short: "Fermés" }];
const OPEN_STATUSES = ["new", "in_progress", "waiting_customer", "waiting_third_party"];
const DONE_STATUSES = ["resolved", "closed"];

// Filters live in the address (dashboard figures, the search palette and a pharmacy's « Support » tab land on the same view).
function useFilters() {
  const [params, setParams] = useSearchParams();
  const pick = (name, allowed) => (allowed.includes(params.get(name)) ? params.get(name) : "");
  const filters = {
    state: pick("state", ["open", "done"]) || (params.get("delay") || params.get("status") ? "" : "open"),
    status: pick("status", [...OPEN_STATUSES, ...DONE_STATUSES]),
    priority: pick("priority", PRIORITIES),
    category: pick("category", Object.keys(CATEGORY_LABELS)),
    responsible: params.get("responsible") || "",
    delay: pick("delay", ["at_risk", "breached"]),
    prospect: params.get("prospect") || "",
    search: params.get("q") || "",
    to_attach: params.get("to_attach") === "1" ? "1" : "",
    tab: params.get("tab") === "stats" ? "stats" : "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = key === "search" ? "q" : key;
        if (value === "" || value == null) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function MobileRow({ item }) {
  return (
    <Link to={`/support/tickets/${item.id}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
      data-testid={`tickets-card-${item.number}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-xs text-slate-500 tabular-nums">{item.number} · {item.pharmacy_name}{item.to_attach && <ToAttachBadge />}</div>
        <div className="font-medium text-slate-900 break-words">{item.title}</div>
        <div className="flex flex-wrap gap-1.5">
          <PriorityBadge priority={item.priority} adjusted={item.priority_adjusted} /><TicketStatusBadge status={item.status} />
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
          <span>{item.responsible_name ? `Responsable : ${item.responsible_name}` : "Sans responsable"}</span>
          <span className="inline-flex gap-1">Délai : <DelayCell ticket={item} /></span>
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

export default function Tickets() {
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const seq = useRef(0);
  const canStats = hasPermission(user, "tickets:view_all");
  const canExport = hasPermission(user, "tickets:export");
  const statsTab = canStats && filters.tab === "stats";

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE };
    for (const key of ["state", "status", "priority", "category", "responsible", "delay", "search"]) if (filters[key]) out[key] = filters[key];
    if (filters.to_attach) out.to_attach = true;
    if (filters.prospect) out.prospect_id = filters.prospect;
    return out;
  }, [filters.state, filters.status, filters.priority, filters.category, filters.responsible, filters.delay, filters.search, filters.prospect, filters.to_attach]); // eslint-disable-line react-hooks/exhaustive-deps

  // A response that arrives after a newer request is dropped.
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/tickets", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(ticketErrorMessage(e, "Impossible de charger les tickets."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => {
    if (statsTab) return undefined;
    load();
    return () => { seq.current += 1; };
  }, [load, statsTab]);

  // The export follows the list's filters (D-71); the server applies the reader's scope again and journals it.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { limit, ...params } = query; // eslint-disable-line no-unused-vars
      const { data: blob } = await api.get("/tickets/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tickets.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Export impossible : plus de 5 000 tickets ou erreur passagère. Affinez les filtres et réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/tickets", { params: { ...query, skip: data.items.length } });
      if (mine !== seq.current) return;
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      if (mine === seq.current) toast.error(ticketErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const statuses = filters.state === "done" ? DONE_STATUSES : filters.state === "open" ? OPEN_STATUSES : [...OPEN_STATUSES, ...DONE_STATUSES];
  const activeFilters = [filters.status, filters.priority, filters.category].filter(Boolean).length;
  const filtered = activeFilters > 0 || !!filters.responsible || !!filters.delay || !!filters.search || !!filters.prospect || !!filters.to_attach;
  const reset = () => { setSearchInput(""); update({ status: "", priority: "", category: "", responsible: "", delay: "", search: "", prospect: "", to_attach: "" }); };
  const quickActive = (view) => Object.entries(view.params).every(([k, v]) => filters[k] === v);
  const toggleQuick = (view) => {
    if (quickActive(view)) update(Object.fromEntries(Object.keys(view.params).map((k) => [k, k === "state" ? "open" : ""])));
    else update({ status: "", responsible: "", delay: "", state: "", to_attach: "", ...view.params });
  };
  const pharmacyName = !filters.prospect ? null
    : (data.items[0]?.prospect_id === filters.prospect ? data.items[0].pharmacy_name : location.state?.pharmacyName) || null;
  // Only the pharmacy filter is on: the empty list is about THIS pharmacy, and the other tab is the next place to look.
  const onlyPharmacy = !!filters.prospect && activeFilters === 0 && !filters.responsible && !filters.delay && !filters.search && !filters.to_attach;

  const selects = (
    <>
      <Select value={filters.status || ALL} onValueChange={(v) => update({ status: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-52" aria-label="Statut" data-testid="tickets-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les statuts</SelectItem>
          {statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.priority || ALL} onValueChange={(v) => update({ priority: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-40" aria-label="Priorité" data-testid="tickets-priority-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Toutes les priorités</SelectItem>
          {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.category || ALL} onValueChange={(v) => update({ category: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-48" aria-label="Catégorie" data-testid="tickets-category-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Toutes les catégories</SelectItem>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500">Les problèmes signalés par les pharmacies, de leur prise en charge à leur résolution.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreateTickets(user) && (
            <Button asChild data-testid="tickets-new">
              <Link to={filters.prospect ? `/support/tickets/nouveau?prospect=${filters.prospect}` : "/support/tickets/nouveau"}><Plus size={16} className="mr-1.5" />Nouveau ticket</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1" role="tablist" aria-label="Vues des tickets" data-testid="tickets-states">
        {STATES.map((s) => (
          <button key={s.id} type="button" role="tab" aria-selected={!statsTab && filters.state === s.id} onClick={() => update({ state: s.id, status: "", delay: "", tab: "" })}
            data-testid={`tickets-state-${s.id}`}
            className={`flex-auto sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${!statsTab && filters.state === s.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <span className="sm:hidden">{s.short}</span><span className="hidden sm:inline">{s.label}</span>
          </button>
        ))}
        {canStats && (
          <button type="button" role="tab" aria-selected={statsTab} onClick={() => update({ tab: "stats" })} data-testid="tickets-tab-stats"
            className={`flex-auto sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${statsTab ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            Indicateurs
          </button>
        )}
      </div>

      {statsTab ? <TicketStats /> : (<>

      {filters.prospect && (
        <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="tickets-pharmacy-filter">
          <span>Pharmacie : <span className="font-medium text-slate-900">{pharmacyName || "cette pharmacie"}</span></span>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => update({ prospect: "" })} aria-label="Retirer le filtre de pharmacie"><X size={14} /></Button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro, titre ou pharmacie…" className="pl-9 h-9"
            aria-label="Rechercher un ticket" data-testid="tickets-search" />
        </div>
        {QUICK_VIEWS.filter((view) => !view.permission || hasPermission(user, view.permission)).map((view) => (
          <Button key={view.id} variant={quickActive(view) ? "default" : "outline"} size="sm" className="h-9 shrink-0" aria-pressed={quickActive(view)}
            onClick={() => toggleQuick(view)} data-testid={`tickets-quick-${view.id}`}>{view.label}</Button>
        ))}
        <div className="hidden md:flex items-center gap-2">{selects}</div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="md:hidden h-9 gap-1.5 shrink-0" aria-label="Filtres" data-testid="tickets-filters-toggle">
              <SlidersHorizontal size={14} />{activeFilters > 0 && <span className="text-xs font-semibold">{activeFilters}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" title="Filtres" className="w-72 space-y-3 md:hidden">{selects}</PopoverContent>
        </Popover>
        {filtered && (
          <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset} data-testid="tickets-reset"><X size={14} className="mr-1" />Effacer</Button>
        )}
      </div>

      {phase === "loading" && (
        <div className="space-y-2" data-testid="tickets-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      )}

      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="tickets-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="tickets-empty">
          <Ticket className="mx-auto text-slate-300" size={26} />
          {onlyPharmacy && filters.state !== "" ? (
            <>
              <h3 className="font-medium text-slate-900">
                {filters.state === "done" ? "Aucun ticket résolu ou clos" : "Aucun ticket ouvert"} pour {pharmacyName || "cette pharmacie"}
              </h3>
              <div className="flex flex-wrap justify-center gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => update({ state: filters.state === "done" ? "open" : "done" })} data-testid="tickets-empty-other-tab">
                  {filters.state === "done" ? "Voir les tickets ouverts" : "Voir les tickets résolus et clos"}
                </Button>
                <Button variant="ghost" size="sm" onClick={reset}>Tous les tickets</Button>
              </div>
            </>
          ) : filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucun ticket ne correspond</h3>
              <p className="text-sm text-slate-500">Modifiez ou effacez les filtres pour élargir la liste.</p>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">{filters.state === "done" ? "Aucun ticket résolu pour le moment" : "Aucun ticket ouvert"}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Quand une pharmacie signale un problème, ouvrez un ticket pour suivre sa prise en charge et sa résolution.</p>
            </>
          )}
        </Card>
      )}

      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="tickets-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead><TableHead>Pharmacie</TableHead><TableHead>Priorité</TableHead><TableHead>Statut</TableHead>
                  <TableHead>Responsable</TableHead><TableHead>Délai</TableHead>
                  <TableHead className="hidden xl:table-cell">Dernier échange</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => navigate(`/support/tickets/${i.id}`)} data-testid={`tickets-row-${i.number}`}>
                    <TableCell className="max-w-[22rem]">
                      <Link to={`/support/tickets/${i.id}`} className="font-medium text-slate-900 hover:underline line-clamp-2" onClick={(e) => e.stopPropagation()}>{i.title}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{i.number} · {CATEGORY_LABELS[i.category]}</div>
                    </TableCell>
                    <TableCell className="text-sm">{i.pharmacy_name}{i.to_attach && <ToAttachBadge />}</TableCell>
                    <TableCell><PriorityBadge priority={i.priority} adjusted={i.priority_adjusted} /></TableCell>
                    <TableCell><TicketStatusBadge status={i.status} /></TableCell>
                    <TableCell className="text-sm">{i.responsible_name || <span className="text-slate-400">Sans responsable</span>}</TableCell>
                    <TableCell><DelayCell ticket={i} testid={`tickets-delay-${i.number}`} /></TableCell>
                    <TableCell className="hidden xl:table-cell text-sm tabular-nums text-slate-600">{formatDateTime(i.last_contact_at) || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="tickets-list-mobile">{data.items.map((i) => <MobileRow key={i.id} item={i} />)}</Card>

          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="tickets-count">{data.items.length} sur {data.total} ticket{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="tickets-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
      </>)}
    </div>
  );
}
